import React, { useState, useEffect, useRef } from "react";
import { useParams , useNavigate} from "react-router-dom";

import "../components/RelativesPage.css";

function RelativesPage() {
  const { patient_id } = useParams();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedRelative, setSelectedRelative] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
const sessionToken = localStorage.getItem('session_token');
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
const navigate = useNavigate();

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch patients
 const loadPatients = async (searchTerm = "") => {
  setSearchLoading(true);
  setError(null);

  try {
    const url = searchTerm
      ? `https://newerp.awasthivitality.com/api/dashboard/master-search?q=${encodeURIComponent(searchTerm)}`
      : "https://newerp.awasthivitality.com/api/dashboard/master-search";

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": sessionToken, // ✅ TOKEN APPLIED TO SEARCH BAR
      },
    });

    if (!response.ok) throw new Error(`Server Error: ${response.status}`);

    const data = await response.json();
if (data.success) {
  const list = Array.isArray(data.data) ? data.data : [];
  setResults(list);

  // ✅ ADD THIS LINE HERE
  setDropdownOpen(true);

} else {
  setError(data.message || "Failed to fetch patients.");
  setResults([]);

  // ✅ ADD THIS ALSO
  setDropdownOpen(false);
}
  } catch (err) {
    setError(err.message || "Failed to fetch patients.");
    setResults([]);
  } finally {
    setSearchLoading(false);
  }
};

  // Load all patients on mount
  useEffect(() => {
    loadPatients();
  }, []);

  // Handle input change with debounce
const handleInputChange = (e) => {
  const value = e.target.value;
  setSearch(value);
  setSelectedRelative(null);
  setError(null);

  // 1. If empty, clear results and hide
  if (!value.trim()) {
    setResults([]);
    setDropdownOpen(false);
    return;
  }

  // 2. Only search if at least 2 characters are typed (makes it "fast")
  if (value.length >= 2) {
    setDropdownOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadPatients(value);
    }, 300); // 300ms is the sweet spot for search
  }
};
  // Open dropdown on input focus
  const handleInputFocus = () => {
    if (results.length > 0) setDropdownOpen(true);
  };

  const handleSelect = (item) => {
    setSearch(item.display_text || item.name || "");
    setDropdownOpen(false);
    setSelectedRelative(item);
    setSuccessMessage("");
    setError(null);
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!selectedRelative) {
    setError("Please select a relative first.");
    return;
  }

  setAddLoading(true);
  setError(null);
  setSuccessMessage("");

  try {
    const response = await fetch(
      "https://newerp.awasthivitality.com/api/relative/add",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": sessionToken,
        },
        body: JSON.stringify({
          patient_id: parseInt(patient_id),
          relative_patient_id: selectedRelative.patient_id,
        }),
      }
    );

    const data = await response.json();

    if (data.success) {
      setSuccessMessage("Relatives Updated");

      // ✅ Redirect to patient details page
      setTimeout(() => {
      navigate(`/patient/details/${patient_id}`);
      }, 1000);
    } else {
      setError(data.message || "Failed to add relative.");
    }
  } catch (err) {
    setError(err.message || "Failed to add relative.");
  } finally {
    setAddLoading(false);
  }
};


  return (
    <div className="relative-page">
      <div className="form-container">
        <h2>Add Relative for Patient {patient_id}</h2>

        {error && (
          <div className="alert alert-danger">
            {error}
            <button type="button" className="close" onClick={() => setError(null)}>
              <span>&times;</span>
            </button>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            {successMessage}
            <button type="button" className="close" onClick={() => setSuccessMessage("")}>
              <span>&times;</span>
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="search-container" ref={dropdownRef}>
            <input
              type="text"
              placeholder="Search patient by name, PR number, mobile..."
              value={search}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              required
            />

            {searchLoading && (
              <div className="search-loading">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            )}
{dropdownOpen && (
  <ul className="relatives-dropdown-list">
    {searchLoading ? (
      <li className="relatives-dropdown-item">Searching...</li>
    ) : results.length > 0 ? (
      results.map((item) => (
        <li 
          key={item.patient_id} 
          onClick={() => handleSelect(item)}
          className="relatives-dropdown-item"
        >
          {item.name} - {item.pr_no} - {item.mobile}
        </li>
      ))
    ) : (
      <li className="relatives-dropdown-item">No matching patients found.</li>
    )}
  </ul>
)}
          </div>

          <button type="submit" disabled={addLoading || !selectedRelative}>
            {addLoading ? "Adding..." : "Submit"}
          </button>
        </form>

        {selectedRelative && (
          <div className="selected-patient-info">
            <h5>Selected Patient:</h5>
            <div className="patient-card">
              <div className="patient-header">
                <h6><strong>{selectedRelative.name || 'Patient'}</strong></h6>
                <span className="badge">{selectedRelative.pr_no}</span>
              </div>
              <div className="patient-body">
                <p><strong>Mobile:</strong> {selectedRelative.mobile}</p>
                {selectedRelative.city && <p><strong>City:</strong> {selectedRelative.city}</p>}
                {selectedRelative.state && <p><strong>State:</strong> {selectedRelative.state}</p>}
                {selectedRelative.country && <p><strong>Country:</strong> {selectedRelative.country}</p>}
                {selectedRelative.branch_name && <p><strong>Branch:</strong> {selectedRelative.branch_name}</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RelativesPage;
